import React, { useState, useRef } from "react";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import LeadForm from "@/components/LeadForm";
import FAQSection from "@/components/FAQSection";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import ScrollReveal, { StaggerContainer, StaggerItem } from "@/components/ScrollReveal";
import CountUp from "@/components/CountUp";
import { WHATSAPP_LINK } from "@/lib/api";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/51bffd07-aa4d-4a7d-95f9-f89bd7c79be4/images/ce7dde93c951a14dfe0e7ad0478f7ac2ab4daaf53cd28e3907cc7445d52e95ba.jpeg";
const EDITORIAL_IMG =
  "https://static.prod-images.emergentagent.com/jobs/51bffd07-aa4d-4a7d-95f9-f89bd7c79be4/images/23583744b7781f8fd417a44e95d62496f4b1af375aa9e9910cae2ce3b39d0aeb.jpeg";
const PROCESO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/51bffd07-aa4d-4a7d-95f9-f89bd7c79be4/images/ad59dce4f3d6548d9a8c1677a1ec242293160c0cfa034a7101d2604d6b4bc121.jpeg";

const ANCLA_STATS = [
  { value: "15+", label: "Instituciones financieras" },
  { value: "$7.5M — $45M+", label: "Rango de operación" },
  { value: "72 hrs", label: "Propuesta ejecutiva" },
  { value: "$0", label: "Costo para ti" },
];

const MODALIDADES = [
  {
    title: "Adquisición",
    desc: "Estructuramos la operación completa: enganche, plazo e institución. Cada variable afecta tu liquidez a futuro.",
  },
  {
    title: "Construcción y Remodelación",
    desc: "Financiamiento por ministraciones con instituciones que conocen los tiempos reales de un proyecto de arquitectura personalizada.",
  },
  {
    title: "Mejora de Hipoteca",
    desc: "Tu hipoteca actual se contrató bajo condiciones que ya cambiaron. Revisamos si hoy existe una estructura mejor — con liquidez adicional cuando aplica.",
  },
  {
    title: "Liquidez",
    desc: "Tu propiedad puede generar capital sin venderla. Analizamos el costo real frente a lo que harás con ese capital.",
  },
];

const PROCESO = [
  {
    title: "Diagnóstico privado",
    desc: "30 a 45 minutos. Revisamos tu perfil financiero, crediticio y fiscal. Confidencialidad absoluta desde el primer intercambio.",
  },
  {
    title: "Propuesta ejecutiva — 72 horas",
    desc: "Las opciones que aplican a tu operación, con condiciones preferenciales y proyecciones a 10, 15 y 20 años. Un documento para decidir con fundamento.",
  },
  {
    title: "Acompañamiento hasta la firma",
    desc: "Gestión integral por un equipo especializado. Tu participación: decidir y firmar.",
  },
];

const DISTINTIVOS = [
  {
    title: "Analizamos tu operación completa",
    desc: "Patrimonio, flujo y régimen fiscal antes de recomendar una institución. La tasa es solo una de las variables que definen el costo real.",
  },
  {
    title: "Condiciones que no se publican",
    desc: "Las mejores estructuras para este segmento se negocian directamente con cada institución. No aparecen en sus canales abiertos.",
  },
  {
    title: "Alineados contigo",
    desc: "Tu interés y el nuestro son el mismo desde el inicio. Las mejores condiciones con el más alto nivel de atención, a cero costo para ti.",
  },
  {
    title: "Visión fiscal integrada",
    desc: "Deducibilidad, estructura del enganche, impacto en tu régimen. La decisión financiera y la fiscal, alineadas desde el principio.",
  },
  {
    title: "Confidencialidad total",
    desc: "Tu expediente opera bajo los estándares de privacidad del segmento premium de cada institución y participante del proceso. Te cuidamos de inicio a fin.",
  },
];

const PERFILES = [
  {
    tag: "Ingresos mixtos",
    desc: "Director o socio con nómina + honorarios + dividendos. La banca estándar no sabe leer ese perfil.",
  },
  {
    tag: "Construcción",
    desc: "Empresario que construye su residencia definitiva y necesita ministraciones ágiles alineadas con el avance de obra.",
  },
  {
    tag: "Liquidez",
    desc: "Propietario con plusvalía acumulada que busca liquidez hipotecaria para reinvertir sin vender.",
  },
  {
    tag: "Binacional",
    desc: "Perfil binacional o con ingresos en dólares — situación que la mayoría de los bancos locales no sabe estructurar.",
  },
  {
    tag: "Sustitución",
    desc: "Hipoteca contratada en 2022–2024 que ya no es competitiva en el entorno de tasas actual.",
  },
];

/* ——— Hero parallax sub-component ——— */
function HeroSection({ openForm }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-end overflow-hidden" data-testid="hero-section">
      <motion.div className="absolute inset-0 z-0" style={{ y: imgY }}>
        <img
          src={HERO_IMG}
          alt="Residencia de lujo moderna"
          className="w-full h-full object-cover"
          loading="eager"
          fetchpriority="high"
          width={1536}
          height={1024}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to top, #1F1F1F 5%, rgba(31,31,31,0.7) 45%, rgba(31,31,31,0.3) 100%)",
          }}
        />
      </motion.div>
      <motion.div className="relative z-10 bf-container pb-20 md:pb-28 lg:pb-32 pt-32" style={{ opacity }}>
        <div className="max-w-3xl">
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[1.05] text-white bf-fade-up">
            Hay hipotecas disponibles.
          </h1>
          <h1
            className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[1.05] italic bf-fade-up bf-delay-1"
            style={{ color: "rgba(255,255,255,0.55)" }}
          >
            Y hay hipotecas diseñadas para ti.
          </h1>
          <p
            className="mt-8 md:mt-10 text-sm md:text-base max-w-lg leading-relaxed bf-fade-up bf-delay-2"
            style={{ color: "rgba(255,255,255,0.5)" }}
          >
            Una hipoteca bien estructurada no solo financia tu propiedad.
            <br />
            Protege el resto de tu patrimonio.
          </p>
          <p
            className="mt-4 text-xs md:text-sm uppercase tracking-[0.15em] font-medium bf-fade-up bf-delay-2"
            style={{ color: "var(--teal)", opacity: 0.7 }}
          >
            Hipotecas Premium para la Zona Metropolitana de Guadalajara — donde la plusvalía residencial crece al 8.9% anual.
          </p>
          <button
            onClick={openForm}
            data-testid="hero-cta-button"
            className="mt-10 bf-btn-coral px-9 py-4 bf-fade-up bf-delay-3"
          >
            Conversemos sobre tu operación
          </button>
        </div>
      </motion.div>
    </section>
  );
}

/* ——— Ancla Stats ——— */
function AnclaSection() {
  return (
    <section
      data-testid="ancla-section"
      className="border-y"
      style={{ borderColor: "rgba(255,255,255,0.06)", background: "#282828" }}
    >
      <div className="bf-container">
        <StaggerContainer stagger={0.1} threshold={0.2} className="grid grid-cols-2 md:grid-cols-4">
          {ANCLA_STATS.map((s, i) => (
            <StaggerItem key={s.label} preset="fadeUp" duration={0.6}>
              <div
                className={`px-6 py-10 md:px-8 md:py-14 ${
                  i < ANCLA_STATS.length - 1 ? "border-r" : ""
                } ${i < 2 ? "border-b md:border-b-0" : ""}`}
                style={{ borderColor: "rgba(255,255,255,0.06)" }}
                data-testid={`ancla-stat-${i}`}
              >
                <div className="font-serif text-2xl md:text-3xl lg:text-4xl text-white tracking-tight">
                  <CountUp value={s.value} duration={2} />
                </div>
                <div
                  className="mt-3 text-[10px] uppercase tracking-[0.22em] font-medium"
                  style={{ color: "rgba(255,255,255,0.35)" }}
                >
                  {s.label}
                </div>
              </div>
            </StaggerItem>
          ))}
        </StaggerContainer>
      </div>
    </section>
  );
}

/* ——— Apertura ——— */
function AperturaSection() {
  return (
    <section
      className="py-24 md:py-36"
      style={{ background: "#1F1F1F" }}
      data-testid="apertura-section"
    >
      <div className="bf-container max-w-4xl text-center">
        <ScrollReveal preset="lineGrow" duration={1} className="mx-auto mb-10" style={{ width: 32, height: 1, background: "var(--teal)" }} />
        <ScrollReveal preset="fadeUp" duration={1} delay={0.15}>
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl tracking-tight leading-snug text-white">
            Una hipoteca compromete tu flujo durante veinte años.{" "}
            <span style={{ color: "rgba(255,255,255,0.3)" }}>
              Bien diseñada, lo hace sin comprometer nada más.
            </span>
          </h2>
        </ScrollReveal>
      </div>
    </section>
  );
}

/* ——— Perfiles ——— */
function PerfilesSection() {
  return (
    <section
      className="relative py-24 md:py-36 overflow-hidden"
      style={{ background: "#232323" }}
      data-testid="perfiles-section"
    >
      {/* Decorative large ghost text */}
      <div
        className="absolute top-8 right-0 font-serif text-[12rem] md:text-[18rem] leading-none select-none pointer-events-none"
        style={{ color: "rgba(255,255,255,0.015)" }}
        aria-hidden="true"
      >
        &amp;tú
      </div>

      <div className="relative z-10 bf-container">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-20">
          {/* Left column — sticky heading */}
          <div className="lg:col-span-4 lg:sticky lg:top-32 lg:self-start">
            <ScrollReveal preset="fadeLeft" duration={0.8}>
              <div className="bf-teal-line mb-6" />
              <p className="bf-eyebrow">¿Te identificas?</p>
              <h2 className="mt-4 font-serif text-3xl sm:text-4xl md:text-5xl tracking-tight leading-[1.1] text-white">
                Perfiles en los que somos especialistas
              </h2>
              <p className="mt-5 text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.35)" }}>
                Si tu situación encaja con alguno de estos escenarios, estamos diseñados para resolverla.
              </p>
            </ScrollReveal>
          </div>

          {/* Right column — profile cards */}
          <StaggerContainer stagger={0.1} threshold={0.05} className="lg:col-span-8">
            {PERFILES.map((p, i) => (
              <StaggerItem key={p.tag} preset="fadeUp" duration={0.7}>
                <div
                  className="group relative border-b py-10 md:py-12 grid grid-cols-12 gap-4 md:gap-8 items-start transition-all duration-500"
                  style={{ borderColor: "rgba(255,255,255,0.07)" }}
                  data-testid={`perfil-${i}`}
                >
                  {/* Ghost number */}
                  <div className="col-span-2 md:col-span-2">
                    <span
                      className="font-serif text-5xl md:text-6xl lg:text-7xl leading-none transition-colors duration-500 group-hover:text-[rgba(4,176,186,0.2)]"
                      style={{ color: "rgba(255,255,255,0.04)" }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  {/* Content */}
                  <div className="col-span-10 md:col-span-10">
                    <span
                      className="inline-block text-[10px] font-bold tracking-[0.25em] uppercase px-3 py-1.5 mb-4 transition-all duration-500 group-hover:bg-[rgba(4,176,186,0.15)]"
                      style={{ background: "rgba(4,176,186,0.07)", color: "var(--teal)", borderRadius: "1px" }}
                    >
                      {p.tag}
                    </span>
                    <p
                      className="text-sm md:text-base leading-relaxed max-w-xl transition-colors duration-500 group-hover:text-white/60"
                      style={{ color: "rgba(255,255,255,0.4)" }}
                    >
                      {p.desc}
                    </p>
                  </div>
                  {/* Hover accent line */}
                  <div
                    className="absolute left-0 top-0 h-full w-[2px] origin-top scale-y-0 transition-transform duration-500 group-hover:scale-y-100"
                    style={{ background: "var(--teal)" }}
                  />
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </div>
    </section>
  );
}

/* ——— Editorial Visual ——— */
function EditorialSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1.08, 1.02, 1.08]);

  return (
    <section ref={ref} className="relative h-[50vh] md:h-[60vh] overflow-hidden">
      <motion.img
        src={EDITORIAL_IMG}
        alt=""
        className="w-full h-full object-cover"
        loading="lazy"
        width={1536}
        height={1024}
        style={{ y, scale }}
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(135deg, rgba(31,31,31,0.85) 0%, rgba(31,31,31,0.25) 60%, rgba(31,31,31,0.6) 100%)",
        }}
      />
      <div
        className="absolute bottom-0 left-0 right-0 h-[2px]"
        style={{ background: "var(--teal)", opacity: 0.4 }}
      />
    </section>
  );
}

/* ——— Modalidades ——— */
function ModalidadesSection({ openForm }) {
  return (
    <section
      className="py-20 md:py-28"
      style={{ background: "#1F1F1F" }}
      data-testid="modalidades-section"
    >
      <div className="bf-container">
        <ScrollReveal preset="fadeUp" duration={0.7}>
          <p className="bf-eyebrow">Modalidades</p>
        </ScrollReveal>
        <StaggerContainer
          stagger={0.12}
          threshold={0.08}
          className="mt-12 grid grid-cols-1 md:grid-cols-2 border-t border-l"
          style={{ borderColor: "rgba(255,255,255,0.08)" }}
        >
          {MODALIDADES.map((m, i) => (
            <StaggerItem key={m.title} preset="fadeUp" duration={0.7}>
              <div
                className="group border-b border-r p-8 md:p-12 transition-all duration-500 hover:bg-white/[0.03]"
                style={{ borderColor: "rgba(255,255,255,0.08)" }}
                data-testid={`modalidad-${i}`}
              >
                <div
                  className="inline-block text-xs font-bold tracking-[0.2em] uppercase mb-4"
                  style={{ color: "var(--teal)" }}
                >
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="font-serif text-xl md:text-2xl text-white tracking-tight group-hover:translate-x-1 transition-transform duration-300">
                  {m.title}
                </h3>
                <p
                  className="mt-4 text-sm leading-relaxed"
                  style={{ color: "rgba(255,255,255,0.45)" }}
                >
                  {m.desc}
                </p>
              </div>
            </StaggerItem>
          ))}
        </StaggerContainer>
        <ScrollReveal preset="fadeUp" delay={0.3} duration={0.6}>
          <div className="mt-14">
            <button
              onClick={openForm}
              data-testid="modalidades-cta-button"
              className="bf-btn-coral px-9 py-4"
            >
              Habla con un especialista
            </button>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}

/* ——— Proceso ——— */
function ProcesoSection() {
  return (
    <section
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="proceso-section"
    >
      <div className="absolute inset-0 z-0">
        <img
          src={PROCESO_IMG}
          alt=""
          className="w-full h-full object-cover"
          loading="lazy"
          width={1536}
          height={1024}
        />
        <div
          className="absolute inset-0"
          style={{ background: "rgba(31,31,31,0.9)" }}
        />
      </div>
      <div className="relative z-10 bf-container">
        <ScrollReveal preset="fadeUp" duration={0.7}>
          <p className="bf-eyebrow">Proceso</p>
        </ScrollReveal>
        <StaggerContainer stagger={0.18} threshold={0.1} className="mt-14 space-y-0">
          {PROCESO.map((p, i) => (
            <StaggerItem key={p.title} preset="fadeUp" duration={0.8}>
              <div
                className="relative border-b py-12 md:py-16 grid md:grid-cols-12 gap-6 md:gap-12 items-start"
                style={{ borderColor: "rgba(255,255,255,0.1)" }}
                data-testid={`proceso-step-${i}`}
              >
                <div className="md:col-span-2">
                  <span
                    className="font-serif text-6xl md:text-7xl lg:text-8xl leading-none"
                    style={{ color: "var(--teal)", opacity: 0.15 }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <div className="md:col-span-10 md:pt-2">
                  <h3 className="font-serif text-xl md:text-2xl text-white tracking-tight">
                    {p.title}
                  </h3>
                  <p
                    className="mt-3 text-sm leading-relaxed max-w-xl"
                    style={{ color: "rgba(255,255,255,0.45)" }}
                  >
                    {p.desc}
                  </p>
                </div>
              </div>
            </StaggerItem>
          ))}
        </StaggerContainer>
      </div>
    </section>
  );
}

/* ——— Distintivos ——— */
function DistintivosSection() {
  return (
    <section
      className="py-24 md:py-32"
      style={{ background: "#1F1F1F" }}
      data-testid="distintivos-section"
    >
      <div className="bf-container">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-20">
          <div className="lg:col-span-4 lg:sticky lg:top-32 lg:self-start">
            <ScrollReveal preset="fadeLeft" duration={0.8}>
              <p className="bf-eyebrow">Lo que nos distingue</p>
              <div className="bf-teal-line mt-5" />
            </ScrollReveal>
          </div>
          <StaggerContainer stagger={0.1} threshold={0.05} className="lg:col-span-8">
            {DISTINTIVOS.map((d, i) => (
              <StaggerItem key={d.title} preset="fadeUp" duration={0.7}>
                <div
                  className="group border-b py-10 md:py-12 grid grid-cols-12 gap-4 md:gap-8 items-baseline"
                  style={{ borderColor: "rgba(255,255,255,0.08)" }}
                  data-testid={`distintivo-${i}`}
                >
                  <div className="col-span-2 md:col-span-1">
                    <span
                      className="text-base md:text-lg font-bold tabular-nums"
                      style={{ color: "var(--teal)", opacity: 0.7 }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <div className="col-span-10 md:col-span-11">
                    <h3 className="font-serif text-lg md:text-xl text-white tracking-tight group-hover:translate-x-1 transition-transform duration-300">
                      {d.title}
                    </h3>
                    <p
                      className="mt-3 text-sm leading-relaxed max-w-2xl"
                      style={{ color: "rgba(255,255,255,0.42)" }}
                    >
                      {d.desc}
                    </p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </div>
    </section>
  );
}

/* ——— Cierre ——— */
function CierreSection({ openForm }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.06, 1]);

  return (
    <section
      ref={ref}
      className="relative py-24 md:py-36 overflow-hidden"
      data-testid="cierre-section"
    >
      <div className="absolute inset-0 z-0 overflow-hidden">
        <motion.img
          src={EDITORIAL_IMG}
          alt=""
          className="w-full h-full object-cover"
          loading="lazy"
          style={{ scale: bgScale, filter: "blur(2px)" }}
        />
        <div
          className="absolute inset-0"
          style={{ background: "rgba(31,31,31,0.92)" }}
        />
      </div>
      <div className="relative z-10 bf-container max-w-3xl text-center">
        <ScrollReveal preset="lineGrow" duration={0.8} className="mx-auto mb-10" style={{ width: 32, height: 1, background: "var(--teal)" }} />
        <ScrollReveal preset="fadeUp" duration={1} delay={0.1}>
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl tracking-tight leading-snug text-white">
            Hay decisiones que admiten corrección.
          </h2>
          <h2
            className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl tracking-tight leading-snug mt-2"
            style={{ color: "rgba(255,255,255,0.3)" }}
          >
            La estructura de una hipoteca a veinte años, raramente.
          </h2>
        </ScrollReveal>
        <ScrollReveal preset="fadeUp" duration={0.8} delay={0.25}>
          <div className="mt-12 flex flex-wrap justify-center gap-4">
            <button
              onClick={openForm}
              data-testid="cierre-cta-button"
              className="bf-btn-coral px-9 py-4"
            >
              Habla con un especialista
            </button>
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cierre-whatsapp-button"
              className="bf-btn-ghost px-9 py-4"
            >
              WhatsApp
            </a>
          </div>
          <p
            className="mt-10 text-[11px] uppercase tracking-[0.2em]"
            style={{ color: "rgba(255,255,255,0.2)" }}
          >
            Guadalajara, Jalisco · Lunes a viernes, 9:00 – 19:00
          </p>
        </ScrollReveal>
      </div>
    </section>
  );
}

/* ——————————————————— MAIN PAGE ——————————————————— */
export default function LandingPage() {
  const [formOpen, setFormOpen] = useState(false);
  const openForm = () => setFormOpen(true);

  return (
    <div className="min-h-screen" style={{ background: "#1F1F1F" }}>
      <Navbar onCtaClick={openForm} />
      <HeroSection openForm={openForm} />
      <AnclaSection />
      <AperturaSection />
      <PerfilesSection />
      <EditorialSection />
      <ModalidadesSection openForm={openForm} />
      <ProcesoSection />
      <DistintivosSection />
      <div style={{ background: "#252525" }}>
        <FAQSection onCtaClick={openForm} />
      </div>
      <CierreSection openForm={openForm} />
      <Footer />
      <WhatsAppFloat />
      <LeadForm open={formOpen} onOpenChange={setFormOpen} />
    </div>
  );
}
